import { axiosReq } from "@/lib/axios";
import { queryClient } from "@/lib/react-query" 
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";

export type ImageBody = {
  image_url: string,
  catalogue_item: string,
  id: number,
  prompt: string,
  wishlist: boolean,
  status?: string
}

export type CatalogImageBody = {
  catalogue: string,
  images: ImageBody[]
}
export const useGetGallery = (open: boolean) => {

  const request = async (pageParam: number): Promise<{
    data: CatalogImageBody[]
  }> => {
    const response = await axiosReq.post('/api/landing/gallery/', {
      page: pageParam
    })
    return response.data
  }

  const { data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status, } = useInfiniteQuery({
      queryKey: ["gallery"],
      queryFn: ({ pageParam = 1 }) => request(pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const hasImages = lastPage.data.some(item => item.images.length > 0);
        if (!hasImages) return undefined;
        return allPages.length + 1;
      },
      enabled: open,
    });

  const gallery = useMemo(() => {
    if (!data) return [];

    const catalogueMap = new Map<string, ImageBody[]>();

    data.pages.forEach(page => {
      page.data.forEach(item => {
        const existingImages = catalogueMap.get(item.catalogue) || [];
        catalogueMap.set(item.catalogue, [...existingImages, ...item.images]);
      });
    });

    return Array.from(catalogueMap.entries()).map(([catalogue, images]) => ({
      catalogue,
      images
    }));
  }, [data]);


  return {
    gallery,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status
  }
}

type ImageUploadArgs = {
  catalogue: string,
  user: string,
  media_file: File,
  prompt: string
}

export const useUploadPostImage = () => {

  const request = async (data: ImageUploadArgs): Promise<{
    image_path: string,
    image_id: number
  }> => {

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const response = await axiosReq.post('/api/approval/upload-post-media/', data, config);
    return response.data;
  };

  const { mutate: uploadImage, data, isSuccess, isPending } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      toast.success("Image uploaded successfully");
      return data;
    },
    onError: (error) => {
      toast.error("Failed to upload image");
      console.error("Image upload error:", error);
    }
  });

  return { uploadImage, isSuccess, data, isPending };
};

export const useGetVideoGallery = (open: boolean) => {
  const request = async (pageParam: number): Promise<{
    data: CatalogImageBody;
  }> => {
    const response = await axiosReq.post('/api/landing/gallery-catalogue/', {
      page: pageParam,
      catalogue: "video"
    });
    return response.data;
  };

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ["video-catalogue"],
    queryFn: ({ pageParam = 1 }) => request(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const hasImages = lastPage.data?.images.length > 0;
      if (!hasImages) return undefined;
      return allPages.length + 1;
    },
    enabled: open // fetch only if open is true and catalogue is provided
  });

  const gallery = useMemo(() => {
    if (!data) return [];

    // Since only one catalogue is returned, flatten all pages' images into a single array
    const allImages: ImageBody[] = data.pages.flatMap(page => page.data.images);
    console.log(allImages)

    return allImages;
  }, [data]);

  return {
    gallery,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  };
};



export type WishlistBody = {
  image_url: string,
  catalogue_item: string,
  id: number,
  prompt: string,
  wishlist: boolean
}

// Custom hook to get wishlisted images
export const useGetWishlist = (open: boolean) => {

  const request = async (pageParam: number): Promise<{
    data: WishlistBody[]
  }> => {
    const response = await axiosReq.post('/api/landing/wishlist/', {
      page: pageParam
    })
    return response.data
  }

  const { data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status, } = useInfiniteQuery({
      queryKey: ["wishlist"],
      queryFn: ({ pageParam = 1 }) => request(pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.data.length === 0) return undefined;
        return allPages.length + 1;
      },
      enabled: open,
    });

  let wishlist = data?.pages.flatMap(page => page.data)

  return {
    wishlist,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status
  }
}

// Custom hook to add wishlist image
export const useWishlistImage = () => {

  const request = async (id: number): Promise<{ message: string }> => {
    const response = await axiosReq.post('/api/landing/set-wishlist/', { id });
    return response.data
  }

  const { mutate: wishlistImage, status } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ["wishlist"] })
        queryClient.invalidateQueries({ queryKey: ["gallery"] })
      }
    }
  })

  return { wishlistImage, status }

}

export const useDeleteImage = () => {

  const request = async (id: number) => {
    const response = await axiosReq.post('/api/landing/delete-image/', { image_id: id });
    return response.data;
  }

  const { mutate: deleteImage, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wishlist"] })
      queryClient.invalidateQueries({ queryKey: ["video-catalogue"] })
      queryClient.invalidateQueries({ queryKey: ["gallery"] })
    }
  })

  return { deleteImage, status }
}

